import React, { Component } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';

import ShowErrors from '../common/ShowErrors';
import { getAlternateLanguageLinks, getBaseName, nl2br } from '../common/Utils';
import { Helmet } from 'react-helmet';
import moment from 'moment';

function stripHtml(value) {
    if (!value) {
        return '';
    }

    return value
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ')
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/\s+/g, ' ')
        .trim();
}

function truncateText(value, maxLength) {
    if (!value || value.length <= maxLength) {
        return value || '';
    }

    return `${value.substring(0, maxLength - 1).trim()}…`;
}

class CircularContent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            circular: null,
            errors: null
        };
    }

    componentDidMount() {
        this.loadData();

        if (typeof window.writeMenu === "function") { 
            window.writeMenu();
        }


    }

    async loadData() {
        const lang = this.props.match.params.lang;

        const apiParams = new URLSearchParams({
            refNo: this.props.refNo,
            lang: lang
        });
        const apiUrl = `${getBaseName()}/api/circular/content?${apiParams}`;

        const options = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        }

        try {            
            const response = await fetch(apiUrl,options);
            if (response.status === 200) {
                const result = await response.json();

                console.log(result);

                this.setState({
                    circular: result
                });
            } else {
                this.props.history.push(`/gateway/${lang}/error`);         
            }
        } catch (error) {
            console.log(error);
        }
    }

    showCircular(circular) {
        const { t } = this.props;
        const lang = this.props.match.params.lang;
        const REACT_APP_APPS_DOMAIN = `${process.env['REACT_APP_APPS_DOMAIN']}`;
        const gaScript = `
            var _gaq = _gaq || []; 
            _gaq.push(['_setAccount', '${process.env['REACT_APP_GA_ID']}']); 
            _gaq.push(['_setDomainName', '${REACT_APP_APPS_DOMAIN}']); 
            _gaq.push(['_trackPageview']); 
            
            (function () { 
            var ga = document.createElement('script'); ga.type = 'text/javascript'; ga.async = true; 
            ga.src = ('https:' == document.location.protocol ? 'https://ssl' : 'http://www') + '.google-analytics.com/ga.js'; 
            var s = document.getElementsByTagName('script')[0]; s.parentNode.insertBefore(ga, s); 
            })();
        `;

        const ga4ScriptId = `${process.env['REACT_APP_GA4_ID']}`;

        const ga4Script = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
        
            gtag('config', '${process.env['REACT_APP_GA4_ID']}');      
        `;

        const plainTitle = stripHtml(circular.title);
        const plainBody = stripHtml(circular.html);
        const metaDescription = truncateText(plainBody || plainTitle, 160);
        const canonicalUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
        const alternateLanguageLinks = getAlternateLanguageLinks();
        const inLanguage = lang === 'TC' ? 'zh-Hant-HK' : 'en-HK';
        const locale = lang === 'TC' ? 'zh_HK' : 'en_HK';
        const defaultImageUrl = 'https://www.sfc.hk/assets/images/common/SFC_Logo_Abbreviation_A3_RGB.png?2';
        const publishedTime = moment(circular.releasedDate, "YYYY-MM-DDTHH:mm:ss").toISOString();
        const modifiedTime = moment(circular.modificationTime, "YYYY-MM-DDTHH:mm:ss").toISOString();
        const sfcOrganization = {
            '@type': 'Organization',
            name: 'Securities and Futures Commission',
            url: `https://${REACT_APP_APPS_DOMAIN}`
        };
        const structuredData = {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: plainTitle,
            description: metaDescription,
            articleBody: plainBody,
            text: plainBody,
            datePublished: publishedTime,
            dateModified: modifiedTime,
            inLanguage,
            identifier: circular.refNo,
            isAccessibleForFree: true,
            mainEntityOfPage: canonicalUrl,
            url: canonicalUrl,
            author: sfcOrganization,
            publisher: sfcOrganization,
            image: [defaultImageUrl],
            thumbnailUrl: defaultImageUrl
        };
        
        const appendixList = []; 
        if (circular.appendixDocList && circular.appendixDocList.length > 0) {

            appendixList.push(
                <p>
                    {t('labels.appendixDoc')}
                    <br/>
                {circular.appendixDocList.map((appendix) =>
                    <Link key={appendix.refNo} to={`openAppendix?refNo=${circular.refNo}&appendix=${appendix.refNo}`}>
                        {appendix.caption}<br/>
                    </Link>
                    
                )}
                </p>
            );
        }

        return (
            <div>
                <Helmet>
                    <title>{plainTitle} | {t('common:sfc')}</title>
                    <meta name='description' content={metaDescription}></meta>
                    <meta name='date' content={t('common:date.metaformatter',{date:moment(circular.releasedDate, "YYYY-MM-DDTHH:mm:ss").toDate()})}></meta>
                    <meta name='robots' content='index,follow,max-image-preview:large'></meta>
                    <link rel='canonical' href={canonicalUrl}></link>
                    {alternateLanguageLinks.map((alternateLanguageLink) => (
                        <link key={alternateLanguageLink.hrefLang} rel='alternate' hreflang={alternateLanguageLink.hrefLang} href={alternateLanguageLink.href}></link>
                    ))}
                    <meta property='og:type' content='article'></meta>
                    <meta property='og:site_name' content={t('common:sfc')}></meta>
                    <meta property='og:locale' content={locale}></meta>
                    <meta property='og:title' content={plainTitle}></meta>
                    <meta property='og:description' content={metaDescription}></meta>
                    <meta property='og:url' content={canonicalUrl}></meta>
                    <meta property='og:image' content={defaultImageUrl}></meta>
                    <meta property='article:published_time' content={publishedTime}></meta>
                    <meta property='article:modified_time' content={modifiedTime}></meta>
                    <meta name='twitter:card' content='summary'></meta>
                    <meta name='twitter:title' content={plainTitle}></meta>
                    <meta name='twitter:description' content={metaDescription}></meta>
                    <meta name='twitter:image' content={defaultImageUrl}></meta>
                    <script type='application/ld+json'>{JSON.stringify(structuredData)}</script>
                    <script>{gaScript}</script>
                    <script async src={`https://www.googletagmanager.com/gtag/js?id=${ga4ScriptId}`}></script>
                    <script>{ga4Script}</script>
                </Helmet>
                <div>
                    <h1 dangerouslySetInnerHTML={{ __html: nl2br(circular.title)}}></h1>
                </div>
                <div>
                    <small>{t('labels.releasedDate',{date:moment(circular.releasedDate, "YYYY-MM-DDTHH:mm:ss").toDate()})}</small>
                </div>
                <br/>
                <br/>
                <div  className="newsc"  dangerouslySetInnerHTML={{ __html: circular.html}}></div>
                <br/>
                <p>
                    <Link to={`openFile?refNo=${circular.refNo}`}>
                        {t('labels.mainAttachmentDnldLink')}
                    </Link>
                </p>
                <br/>
                {appendixList}
                <div>
                    <small>{t('labels.lastUpdateDate',{date:moment(circular.modificationTime, "YYYY-MM-DDTHH:mm:ss").toDate()})}</small>
                </div>
            </div>
        );
    }

    render() {
        const { circular, errors } = this.state;
        window.scrollTo(0, 0);
        if (circular) {
            return this.showCircular(circular);
        } else if (errors) {
            return (<ShowErrors errors={errors}/>);
        } else {
            return (<span>Loading...</span>);
        }
    }

}

export default withRouter(withTranslation()(CircularContent));